import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Field, FormikErrors } from 'formik';
import styled from 'styled-components';
import {
  EndorsementServiceFeaturesGetAllCertificatesGetAllCertificatesDto as CertificateDto,
  EndorsementServiceFeaturesGetApplicationGroupByUidAttachment,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { FormDate, FormInput, FormSelect, InputError } from '@sdir/blueprint.aps/lib/components';
import { sortListByAttribute } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { FileUpload, SdiTrash, SelectOption, TextLink } from '@sdir/sds';
import FileName from '../Atoms/FileName';
import { ApplicationType } from '../../services/contexts/CurrentApplicationContext';

interface AddedCertificateProps {
  allCertificates: CertificateDto[];
  application: ApplicationType;
  authoritiesOptions: SelectOption[];
  certificateOptions: SelectOption[];
  filteredCertificateOptions: SelectOption[];
  certificate: CertificateInput;
  errors?: FormikErrors<any>;
  index: number;
  selectedCategory?: string;
  onCategorySelected: (categoryUid?: string) => void;
  setFieldValue: (field: string, value: any) => void;
}

export type CertificateInput = {
  applicationUid?: string | null;
  certificate?: string | null;
  certificateFiles?: AttachmentBaseInput[] | null;
  certificateNumber?: string | null;
  previousAttachments?: EndorsementServiceFeaturesGetApplicationGroupByUidAttachment[] | null;
  expirationDate?: string | null;
  issuedBy?: string | null;
  requestedCertificate?: string | null;
};

export type AttachmentBaseInput = {
  file?: File | null;
  uid?: string;
  name?: string;
};

const AddedCertificate: React.FC<AddedCertificateProps> = ({
  allCertificates,
  application,
  authoritiesOptions,
  certificateOptions,
  filteredCertificateOptions,
  certificate,
  errors,
  index,
  selectedCategory,
  onCategorySelected,
  setFieldValue,
}) => {
  const intl = useIntl();
  const [groupOptions, setGroupOptions] = useState<SelectOption[]>();
  const [attachments, setAttachments] = useState<AttachmentBaseInput[]>();

  const onChangeExistingCertificate = useCallback(
    (value: string) => {
      if (!allCertificates) return;
      const group = allCertificates?.find((g) => g?.certificates?.some((c) => c.uid === value));
      onCategorySelected(group?.categoryUid);
      const groupItems =
        group?.certificates?.map((f) => ({
          name: f?.certificateName || '',
          value: f?.uid || '',
        })) || [];
      setGroupOptions(sortListByAttribute(groupItems, 'name'));
    },
    [allCertificates, intl?.locale, onCategorySelected]
  );

  const onFilesChanged = (files: File[]) => {
    const total = attachments ? [...attachments] : [];
    const thisFile = files?.[0];
    total.push({
      file: thisFile,
      name: thisFile.name,
      uid: '',
    });
    setFieldValue(`certificates.${index}.certificateFiles`, total);
    setAttachments(total);
  };

  const onRemoveFile = (a: AttachmentBaseInput) => {
    let total = attachments ? [...attachments] : [];
    total = total.filter((att) => (a.uid ? att.uid !== a.uid : a.name !== att.name));
    setFieldValue(`certificates.${index}.certificateFiles`, total);
    setAttachments(total);
  };

  useEffect(() => {
    const certificateUid = application?.certificates?.[index]?.uid;
    if (certificateUid) {
      onChangeExistingCertificate(certificateUid);
    }
  }, [application?.certificates]);

  useEffect(() => {
    // Special logic for preventing radio endorsements together with other endorsements
    // Remove this when we allow it or have a better solution
    const radioCategory = '5f9aa57b-9da9-4f4d-a306-1e206fd19055';
    const groupExisting = allCertificates?.find((g) =>
      g?.certificates?.some((c) => c.uid === certificate?.certificate)
    );
    const groupRequested = allCertificates?.find((g) =>
      g?.certificates?.some((c) => c.uid === certificate?.requestedCertificate)
    );
    if (
      (selectedCategory === radioCategory && groupRequested?.categoryUid !== radioCategory) ||
      (selectedCategory !== radioCategory && groupRequested?.categoryUid === radioCategory)
    ) {
      if (groupExisting?.categoryUid !== selectedCategory) {
        setGroupOptions([]);
      }
    }
  }, [selectedCategory, certificate, allCertificates]);

  useEffect(() => {
    setAttachments(certificate?.certificateFiles || []);
  }, [certificate?.certificateFiles]);

  return (
    <FormContent>
      <Left>
        <Field
          name={`certificates.${index}.certificate`}
          label={intl.formatMessage({
            id: 'application.certificate.certificate',
          })}
          component={FormSelect}
          onChange={onChangeExistingCertificate}
          grouped
          searchable
          options={index === 0 ? certificateOptions : filteredCertificateOptions}
        />
        <Field
          name={`certificates.${index}.issuedBy`}
          label={intl.formatMessage({
            id: 'application.certificate.issuedBy',
          })}
          component={FormSelect}
          searchable
          options={authoritiesOptions}
        />
        <div>
          <FileUpload
            allowedTypes=".png,.jpg,.jpeg,.pdf"
            buttonLabel={intl.formatMessage({
              id: 'application.certificate.nationalcertificate',
            })}
            onFilesSelected={onFilesChanged}
          />
          {attachments?.map((a) => (
            <FileItem>
              <FileName key={a.uid || a.name} fileName={a.name || ''} />
              <TextLink icon={<SdiTrash size="s" />} text="" onClick={() => onRemoveFile(a)} />
            </FileItem>
          ))}
          {errors?.[`certificateFiles.${index}`] && (
            <InputError>{errors?.[`certificateFiles.${index}`]}</InputError>
          )}
          {errors?.[`certificates[${index}].attachmentFile`] && (
            <InputError>{errors?.[`certificates[${index}].attachmentFile`]}</InputError>
          )}
        </div>
        <Field
          name={`certificates.${index}.requestedCertificate`}
          label={intl.formatMessage({
            id: 'application.certificate.requestedCertificate',
          })}
          component={FormSelect}
          grouped
          searchable
          options={groupOptions || (index === 0 ? certificateOptions : filteredCertificateOptions)}
        />
      </Left>
      <Right>
        <Field
          name={`certificates.${index}.certificateNumber`}
          label={intl.formatMessage({
            id: 'application.certificate.certificateNumber',
          })}
          component={FormInput}
        />
        <Field
          name={`certificates.${index}.expirationDate`}
          label={intl.formatMessage({
            id: 'application.certificate.expiryDate',
          })}
          component={FormDate}
          locale={intl.locale}
          showYearDropdown
          showMonthDropdown
        />
      </Right>
    </FormContent>
  );
};

export default AddedCertificate;

const FormContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;
  margin-right: 30px;

  & > * {
    margin-bottom: 20px;
  }

  #certificate div[open],
  #issuedBy div[open],
  #requestedCertificate div[open] {
    width: max-content;
    max-width: 100vw;
  }
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  width: 300px;

  & > * {
    margin-bottom: 20px;
  }
`;

const FileItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
